import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { SolarArrayModel } from '../client/ders'
import { useGetSolarArraysQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { SolarArrayCacheRecord } from './storage/schema'
import { useGateValue } from '~/configs/statsig'

const SOLAR_ARRAYS_PAGE_SIZE = 1000

// Stable reference for the gated-off path (the common case for most orgs), so a
// fresh [] doesn't bust the manager's useMemo and re-render every accessor.
const EMPTY_SOLAR_ARRAYS: SolarArrayModel[] = []

export type SolarArraysDataManagerContextValue = {
  data: SolarArrayModel[]
  isLoading: boolean
  isFetching: boolean
}

function useSolarArraysQuery(enabled: boolean, options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetSolarArraysQuery(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: SOLAR_ARRAYS_PAGE_SIZE
    },
    combineSkip(!principalOrgId || !enabled, options)
  )

  return {
    solarArrays: data?.data,
    isLoading,
    isFetching
  }
}

export default function useSolarArraysDataManager(service: IndexedDbService) {
  // Solar is behind the solar-in-ui gate. Unlike the always-on DER managers,
  // this both skips the fetch AND withholds any hydrated cache for orgs without
  // the feature, so a cache populated in a prior gated-on session can't leak
  // solar arrays into surfaces that no longer check the gate (site rollups,
  // Cmd+K) while detail pages still 404.
  const hasSolarInUi = useGateValue('solar-in-ui')
  const { solarArrays, isLoading, isFetching } = useSolarArraysQuery(hasSolarInUi)

  const data = useSolarArraysPersistence(service, solarArrays, hasSolarInUi)

  return useMemo<SolarArraysDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useSolarArraysPersistence(
  service: IndexedDbService,
  solarArrays: SolarArrayModel[] | undefined,
  enabled: boolean
): SolarArrayModel[] {
  const [hydratedSolarArrays, setHydratedSolarArrays] = useState<SolarArrayModel[]>([])

  useEffect(() => {
    // Don't surface a previously-cached set when the feature is gated off.
    if (!enabled) {
      setHydratedSolarArrays([])
      return
    }

    async function hydrateSolarArraysCache() {
      try {
        const records = await service.getAll('solarArrays')
        setHydratedSolarArrays(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate solar arrays cache', error)
      }
    }

    void hydrateSolarArraysCache()
  }, [service, enabled])

  useEffect(() => {
    if (!solarArrays) return

    const fetchedAt = Date.now()
    const records: SolarArrayCacheRecord[] = solarArrays.map((solarArray) => ({
      id: solarArray.id,
      payload: solarArray,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('solarArrays', records)
      } catch (error) {
        logger.error('Failed to persist solar arrays snapshot', error)
      }
    }

    void replaceRecords()
  }, [solarArrays, service])

  return enabled ? (solarArrays ?? hydratedSolarArrays) : EMPTY_SOLAR_ARRAYS
}
